import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useRef, useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import { ArrowRight, Play, AlertTriangle, Target, TrendingUp, Users, CheckCircle2, ChevronDown } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.15], [1, 0.95]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden">
      {/* Transparent Navbar for video header */}
      <Navbar transparent={true} />
      
      {/* Video Hero Section */}
      <VideoHeroSection opacity={opacity} scale={scale} navigate={navigate} />
      
      {/* The Principle */}
      <PrincipleSection />
      
      {/* AI as Diagnostic */}
      <DiagnosticSection navigate={navigate} />
      
      {/* The Gap */}
      <GapSection />
      
      {/* Positioning */}
      <PositioningSection />
      
      {/* Process */}
      <ProcessSection />
      
      {/* Final CTA */}
      <FinalCTASection navigate={navigate} />
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

const VideoHeroSection = ({ opacity, scale, navigate }) => {
  const [lineIndex, setLineIndex] = useState(0);
  const { scrollY } = useScroll();
  
  // Parallax effect - video moves slower than scroll
  const videoY = useTransform(scrollY, [0, 1000], [0, 300]);
  const videoScale = useTransform(scrollY, [0, 500], [1, 1.1]);
  
  const lines = [
    { text: "We don't shoot videos.", color: 'text-white' },
    { text: "We build video-first marketing systems", color: 'text-gray-300' }
  ];

  useEffect(() => {
    if (lineIndex < lines.length) {
      const timer = setTimeout(() => setLineIndex(lineIndex + 1), 400);
      return () => clearTimeout(timer);
    }
  }, [lineIndex, lines.length]);

  return (
    <motion.section 
      style={{ opacity, scale }}
      className="relative h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Video Background with Parallax */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ y: videoY, scale: videoScale }}
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute w-full h-full object-cover"
        >
          <source src="https://customer-assets.emergentagent.com/job_59509bd9-c32d-43e1-958e-cf3cb28360ae/artifacts/95bvgxap_video.mp4" type="video/mp4" />
        </video>
        {/* Teal glow overlay */}
        <div className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] rounded-full bg-[#38B2AC]/15 blur-[120px] pointer-events-none" />
        {/* Orange glow overlay */}
        <div className="absolute bottom-1/4 -right-1/4 w-[600px] h-[600px] rounded-full bg-[#DD7638]/15 blur-[120px] pointer-events-none" />
      </motion.div>
      
      {/* Dark overlay - stays fixed */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/60 via-black/50 to-[#0A0A0A]" />

      <div className="max-w-6xl mx-auto relative z-10 text-center px-6 md:px-12">
        <div className="mb-12 space-y-3">
          {lines.map((line, index) => (
            <motion.h1
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={index <= lineIndex ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className={`text-4xl md:text-6xl lg:text-7xl font-bold ${line.color}`}
              data-testid="hero-title"
            >
              {line.text}
            </motion.h1>
          ))}
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={lineIndex >= 2 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold flame-glow">
              that convert.
            </h1>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="text-xl md:text-2xl text-gray-300 mb-16 max-w-3xl mx-auto"
        >
          For brands that value premium storytelling, strategic content, and measurable impact.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <button
            onClick={() => navigate('/lead-form')}
            className="group px-10 py-5 bg-[#DD7638] hover:bg-[#F6AD55] text-white font-semibold uppercase tracking-wider transition-all duration-300 flex items-center gap-3"
            data-testid="schedule-call-button"
          >
            Schedule a Strategy Call
            <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
          </button>
          
          <button
            onClick={() => document.getElementById('discovery-section').scrollIntoView({ behavior: 'smooth' })}
            className="group px-10 py-5 border-2 border-white/30 hover:border-[#DD7638]/50 hover:bg-white/5 text-white font-semibold uppercase tracking-wider transition-all duration-300 flex items-center gap-3"
            data-testid="see-how-button"
          >
            See Our Work
            <Play size={20} className="group-hover:scale-110 transition-transform" />
          </button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5 }}
        className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="flex flex-col items-center gap-2 text-white/50"
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <ChevronDown size={24} />
        </motion.div>
      </motion.div>
    </motion.section>
  );
};

const PrincipleSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const pillars = [
    { title: "Attention", desc: "Be seen by the right people, at the right time" },
    { title: "Trust", desc: "Build credibility that converts strangers to believers" },
    { title: "Leads", desc: "Create a pipeline that feeds your growth consistently" }
  ];

  return (
    <section ref={ref} className="py-32 px-6 md:px-12 relative bg-gradient-to-b from-transparent to-[#DD7638]/5" data-testid="principle-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-[#38B2AC] uppercase tracking-widest text-sm font-semibold mb-4 block">The System You Need</span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">
            A marketing engine that consistently generates
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {pillars.map((pillar, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              className="glass-effect p-10 text-center"
            >
              <h3 className="text-3xl font-bold text-[#DD7638] mb-4">{pillar.title}</h3>
              <p className="text-gray-400">{pillar.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="glass-effect p-12 md:p-16 text-center"
        >
          <p className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-6">
            Most brands generate attention, but struggle to convert it into real business outcomes.
          </p>
          <p className="text-2xl md:text-3xl font-bold text-white">
            We build <span className="text-[#DD7638]">video-led systems</span> that turn attention into measurable, consistent revenue.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

const DiagnosticSection = ({ navigate }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const diagnostics = [
    {
      icon: <Target size={32} />,
      title: "Content System Builder",
      description: "Build a content strategy that works for your brand. Map out what's missing and what's next.",
      tool: "/signup",
      color: "#DD7638"
    },
    {
      icon: <TrendingUp size={32} />,
      title: "Marketing Gap Detector",
      description: "Take our quiz to uncover opportunities. Get a score and actionable insights.",
      tool: "/signup",
      color: "#38B2AC"
    },
    {
      icon: <Play size={32} />,
      title: "AI Storyboard Generator",
      description: "Visualize your video concepts before production. See what's possible.",
      tool: "/signup",
      color: "#9F7AEA"
    },
    {
      icon: <Users size={32} />,
      title: "Brand Transformation",
      description: "Map where your brand is today vs. where it could be in 90 days.",
      tool: "/signup",
      color: "#F6AD55"
    }
  ];

  return (
    <section id="discovery-section" ref={ref} className="py-32 px-6 md:px-12 relative" data-testid="diagnostic-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-20"
        >
          <span className="text-[#38B2AC] uppercase tracking-widest text-sm font-semibold mb-4 block">AI Strategy Tools</span>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Uncover Your Marketing Reality
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Before we build your system, we need to understand where you are. Our AI tools reveal the gaps, opportunities, and blind spots in your current marketing.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {diagnostics.map((diagnostic, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              onClick={() => navigate(diagnostic.tool)}
              className="group strategic-card glass-effect p-8 hover:bg-white/5 cursor-pointer"
              data-testid={`diagnostic-card-${index}`}
            >
              <div 
                className="mb-6 p-3 rounded-lg w-fit group-hover:scale-110 transition-transform duration-300"
                style={{ backgroundColor: `${diagnostic.color}20` }}
              >
                <div style={{ color: diagnostic.color }}>{diagnostic.icon}</div>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{diagnostic.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">{diagnostic.description}</p>
              <div className="font-semibold flex items-center gap-2 text-sm" style={{ color: diagnostic.color }}>
                Run the Strategy <ArrowRight size={16} />
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-16 text-center"
        >
          <button
            onClick={() => navigate('/signup')}
            className="group px-10 py-5 bg-[#DD7638] hover:bg-[#F6AD55] text-white font-semibold uppercase tracking-wider transition-all duration-300 flex items-center gap-3 mx-auto"
          >
            Start Building
            <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
          </button>
          <p className="text-gray-500 text-sm mt-4">Instant access to strategy tools.</p>
        </motion.div>
      </div>
    </section>
  );
};

const GapSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const questions = [
    { q: "What does success look like in 6-12 months?", area: "Business Context" },
    { q: "Who is your ideal customer and how do they find you?", area: "Target Audience" },
    { q: "What marketing activities are working and what's not?", area: "Current Reality" },
    { q: "How are you currently using video in your marketing?", area: "Video Maturity" },
    { q: "Where do prospects drop off in your customer journey?", area: "Conversion" },
    { q: "Are you building authority or chasing trends?", area: "Long-term Vision" }
  ];

  return (
    <section ref={ref} className="py-32 px-6 md:px-12 relative bg-gradient-to-b from-[#DD7638]/5 to-transparent" data-testid="gap-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <span className="text-[#38B2AC] uppercase tracking-widest text-sm font-semibold mb-4 block">Our Discovery Process</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            The Right Questions Reveal Everything
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            We don't start with solutions. We start by understanding your business, audience, goals, and current marketing reality.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12"
        >
          {questions.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.1 * index }}
              className="glass-effect p-6 flex items-start gap-4"
            >
              <div className="w-2 h-2 bg-[#DD7638] rounded-full mt-3 flex-shrink-0"></div>
              <div>
                <p className="text-white text-lg mb-1">{item.q}</p>
                <p className="text-[#38B2AC] text-sm uppercase tracking-wider">{item.area}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
          className="glass-effect p-12 text-center"
        >
          <p className="text-2xl md:text-3xl font-semibold text-white mb-4">
            "How valuable would a system be that consistently generates attention, trust, and leads?"
          </p>
          <p className="text-gray-400 mb-8">— The alignment question we ask every client</p>
          <button
            onClick={() => window.location.href = '/lead-form'}
            className="px-12 py-5 bg-[#DD7638] hover:bg-[#F6AD55] text-white font-bold uppercase tracking-wider transition-all inline-flex items-center gap-3"
          >
            Start Your Discovery
            <ArrowRight size={20} />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

const PositioningSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const comparisons = [
    { wrong: "Chasing trends", right: "Building authority" },
    { wrong: "One-off videos", right: "Content systems" },
    { wrong: "Relying on people", right: "Relying on systems" },
    { wrong: "Hoping it works", right: "Measuring what matters" }
  ];

  return (
    <section ref={ref} className="py-32 px-6 md:px-12 relative" data-testid="positioning-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <span className="text-[#38B2AC] uppercase tracking-widest text-sm font-semibold mb-4 block">Long-term Vision</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Where Do You Want to Be in 2-3 Years?
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            The difference between businesses that scale and those that stall is simple: systems over tactics.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12"
        >
          {comparisons.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 * index }}
              className="glass-effect p-8 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <span className="text-red-400 line-through">{item.wrong}</span>
              </div>
              <ArrowRight className="text-[#DD7638]" size={20} />
              <div className="flex items-center gap-3">
                <span className="text-[#38B2AC] font-semibold">{item.right}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
          className="glass-effect p-12 md:p-16 text-center"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
            What would your marketing engine achieve every month?
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <CheckCircle2 className="text-[#38B2AC] mx-auto mb-3" size={32} />
              <p className="text-gray-300">Consistent content that builds brand</p>
            </div>
            <div className="text-center">
              <CheckCircle2 className="text-[#38B2AC] mx-auto mb-3" size={32} />
              <p className="text-gray-300">Video that converts attention to trust</p>
            </div>
            <div className="text-center">
              <CheckCircle2 className="text-[#38B2AC] mx-auto mb-3" size={32} />
              <p className="text-gray-300">Leads that flow without you chasing</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const ProcessSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const steps = [
    { number: '01', title: 'Discovery & Diagnosis', desc: 'We understand your business, audience, goals, and current marketing reality through AI-powered diagnostics' },
    { number: '02', title: 'Strategy Design', desc: 'We map your customer journey, identify bottlenecks, and design a system that fits your budget and timeline' },
    { number: '03', title: 'System Build', desc: 'Video production, content creation, and distribution infrastructure — engineered to scale' },
    { number: '04', title: 'Optimize & Grow', desc: 'Continuous measurement, testing, and refinement based on real data and results' }
  ];

  return (
    <section ref={ref} className="py-32 px-6 md:px-12 relative bg-gradient-to-b from-transparent to-[#DD7638]/5" data-testid="process-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-20"
        >
          <span className="text-[#38B2AC] uppercase tracking-widest text-sm font-semibold mb-4 block">How We Work</span>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            From Discovery to Scalable System
          </h2>
          <p className="text-xl text-gray-400">Built for your timeline, budget, and growth ambitions</p>
        </motion.div>

        <div className="space-y-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: index * 0.15 }}
              className="glass-effect p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 hover:bg-white/5 transition-all duration-300"
              data-testid={`process-step-${index}`}
            >
              <div className="text-6xl font-bold text-[#DD7638]/20 min-w-[100px]">{step.number}</div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">{step.title}</h3>
                <p className="text-gray-400">{step.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FinalCTASection = ({ navigate }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="py-32 px-6 md:px-12 relative" data-testid="final-cta-section">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.8 }}
        className="max-w-5xl mx-auto text-center glass-effect p-16 md:p-24"
      >
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 leading-tight">
          Designed for brands investing in ongoing video strategy and measurable results.
        </h2>
        <p className="text-xl md:text-2xl text-gray-300 mb-12">
          Run a diagnostic. See where your marketing leaks. Then let's build the system that fixes it.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-12 py-6 bg-[#DD7638] hover:bg-[#F6AD55] text-white text-lg font-semibold uppercase tracking-wider transition-all duration-300 inline-flex items-center gap-3"
            data-testid="explore-ai-tools-button"
          >
            Explore Our AI Tools
            <ArrowRight size={24} />
          </button>
          <button
            onClick={() => navigate('/lead-form')}
            className="px-12 py-6 border-2 border-white/30 hover:border-white text-white text-lg font-semibold uppercase tracking-wider transition-all duration-300"
            data-testid="start-call-button"
          >
            Schedule a Call
          </button>
        </div>
      </motion.div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="py-12 px-6 md:px-12 border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto text-center">
        <p className="text-gray-600">&copy; 2025 Toasty Media. All rights reserved.</p>
      </div>
    </footer>
  );
};

export default HomePage;
